<template>
  <vx-card>
    <b-alert variant="info" show class="mb-2">
      <div class="alert-body">
        Um cadastro — profissional autônomo ou estabelecimento — só aparece no marketplace e só pode
        vender depois de aprovado aqui. A aprovação exige o cadastro completo — a lista de pendências
        aparece ao abrir cada um.
      </div>
    </b-alert>

    <!-- Abas por natureza do fornecedor. O recorte NE continua imposto no
         servidor (rota /professionals/grid); a aba de estabelecimentos usa o
         grid geral de legal_entities filtrado por person_type. -->
    <div class="mb-2 flex flex-row gap-2">
      <b-button
        :variant="personType === 'NE' ? 'primary' : 'outline-primary'"
        size="sm"
        @click="switchType('NE')">
        Profissionais
      </b-button>
      <b-button
        :variant="personType === 'LE' ? 'primary' : 'outline-primary'"
        size="sm"
        class="ml-1"
        @click="switchType('LE')">
        Estabelecimentos
      </b-button>
    </div>

    <Grid ref="grid"
          :key="personType"
          :service="gridService"
          :forceFilters="gridFilters"
          :hide_actions="true"
          route_name="professionals">
      <template #gridThead="thead">
        <vs-th
          v-for="(column, key) in thead.data"
          :key="key"
          :sort-key="thead.methods.getColumnSortKey(key)"
          sort-status="asc">{{ columnLabel(thead.methods.getOriginalColumnName(column)) }}</vs-th>
        <vs-th>Habilitação</vs-th>
        <vs-th>Ação</vs-th>
      </template>
      <template #gridData="gridData">
        <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in gridData.data">
          <vs-td
            :data="gridData.data[indextr].id"
            v-for="(column, key) in gridData.columns"
            :key="key">
            <div :class="gridData.methods.getColumnCss(gridData.data[indextr], key)">
              <div :key="key" v-html-safe="`<div>${gridData.methods.getColumnData(gridData.data[indextr],key)}</div>`"/>
            </div>
          </vs-td>
          <vs-td>
            <b-badge :variant="kycVariant(gridData.data[indextr].kyc_status)">
              {{ kycLabel(gridData.data[indextr].kyc_status) }}
            </b-badge>
          </vs-td>
          <vs-td>
            <div class="flex flex-row gap-2">
              <b-button size="sm" variant="outline-primary" @click="openReview(gridData.data[indextr])">
                Analisar
              </b-button>
            </div>
          </vs-td>
        </vs-tr>
      </template>
    </Grid>

    <b-modal
      v-model="showReview"
      :title="`Analisar: ${selected ? selected.trading_name : ''}`"
      size="lg"
      hide-footer>
      <div v-if="selected">
        <p class="mb-1">
          <strong>{{ selected.document_label || 'Documento' }}:</strong> {{ selected.document_fmt || selected.cnpj_formmated }}
          &nbsp;·&nbsp;
          <strong>Situação:</strong> {{ kycLabel(selected.kyc_status) }}
        </p>

        <h5 class="mt-2">Pendências do cadastro</h5>
        <ul class="list-unstyled">
          <li v-for="item in checklist" :key="item.key" class="mb-25">
            <feather-icon
              :icon="item.ok ? 'CheckCircleIcon' : 'AlertCircleIcon'"
              :class="item.ok ? 'text-success' : 'text-danger'"
              class="h-4 w-4 mr-50"/>
            <span :class="item.ok ? '' : 'text-danger'">{{ item.label }}</span>
          </li>
        </ul>
        <p class="text-muted" v-if="!hasPendings">Nenhuma pendência — o cadastro pode ser aprovado.</p>

        <!--
          A checklist diz se está completo; isto diz o QUE foi preenchido, que é
          o que sustenta a decisão. Sob clique, e não aberto por padrão: a leitura
          expõe CPF e contato do responsável e fica registrada no log de
          privacidade — abrir para todo mundo que só passa os olhos na fila
          encheria a auditoria de acesso que ninguém pediu.
        -->
        <b-button
          size="sm"
          variant="outline-primary"
          class="mt-1"
          :disabled="loadingDetails"
          @click="toggleDetails">
          <feather-icon :icon="showDetails ? 'ChevronUpIcon' : 'FileTextIcon'" class="mr-50"/>
          {{ showDetails ? 'Ocultar dados do cadastro' : 'Ver dados do cadastro' }}
        </b-button>

        <div v-if="showDetails" class="kyc-details mt-2">
          <p v-if="loadingDetails" class="text-muted mb-0">Carregando...</p>

          <template v-else-if="details">
            <div v-for="bloco in detailBlocks" :key="bloco.titulo" class="mb-2">
              <h6 class="text-primary mb-50">{{ bloco.titulo }}</h6>
              <p v-if="!bloco.linhas.length" class="text-muted mb-0">
                <small>Não informado.</small>
              </p>
              <table v-else class="kyc-details-table">
                <tr v-for="linha in bloco.linhas" :key="linha.rotulo">
                  <th>{{ linha.rotulo }}</th>
                  <td>{{ linha.valor }}</td>
                </tr>
              </table>
            </div>
          </template>

          <p v-else class="text-danger mb-0"><small>Não foi possível carregar os dados do cadastro.</small></p>
        </div>

        <b-form-group label="Parecer / motivo" label-for="notes" class="mt-2">
          <b-form-textarea id="notes" v-model="notes" rows="3"
                           placeholder="Obrigatório ao recusar. Vai no e-mail de resposta ao parceiro."/>
        </b-form-group>

        <div class="d-flex justify-content-end gap-2 mt-2">
          <b-button variant="outline-secondary" @click="showReview = false">Fechar</b-button>
          <b-button variant="outline-danger" class="ml-1" @click="reject">Recusar</b-button>
          <b-button variant="primary" class="ml-1" :disabled="hasPendings" @click="approve">
            Aprovar
          </b-button>
        </div>
      </div>
    </b-modal>
  </vx-card>
</template>

<script>
import { BButton, BBadge, BAlert, BModal, BFormGroup, BFormTextarea } from 'bootstrap-vue'
import ProfessionalService from '@/services/api/ProfessionalService'
import LegalEntityService from '@/services/api/LegalEntityService'

/**
 * Fila única de habilitação (KYC) com abas por natureza do fornecedor.
 *
 * As ações do modal usam as rotas neutras /kyc/* — o backend decide os itens
 * da checklist pelo person_type, então analisar um estabelecimento e um
 * profissional é o mesmo gesto. A decisão dispara o e-mail de resposta
 * (KycApproved/KycRejected) para o responsável.
 */
export default {
  components: {
    BButton,
    BBadge,
    BAlert,
    BModal,
    BFormGroup,
    BFormTextarea,
  },
  data: () => ({
    professionalService: null,
    legalEntityService: null,
    personType: 'NE',
    // A fila é o que está pendente de análise; recusados continuam acessíveis
    // pela tela de gestão.
    kycFilter: 'PE',
    showReview: false,
    selected: null,
    checklist: [],
    details: null,
    showDetails: false,
    loadingDetails: false,
    notes: '',
    kycLabels: {
      PE: 'Pendente',
      AP: 'Aprovado',
      RJ: 'Recusado',
    },
  }),
  computed: {
    hasPendings() {
      return this.checklist.some(item => !item.ok)
    },
    gridService() {
      // NE: rota própria que força person_type no servidor. LE: grid geral de
      // legal_entities com o filtro explícito abaixo.
      return this.personType === 'NE' ? this.professionalService : this.legalEntityService
    },
    gridFilters() {
      const filters = { kyc_status: this.kycFilter }
      if (this.personType === 'LE') filters.person_type = 'LE'
      return filters
    },
    /**
     * Os dados do cadastro em blocos legíveis. Campo vazio não vira linha: o
     * que interessa ao analista é o que FOI informado, e uma tabela cheia de
     * "—" esconde justamente isso.
     */
    detailBlocks() {
      if (!this.details) return []

      const linha = (rotulo, valor) => (this.isEmpty(valor) ? null : { rotulo, valor })
      const bloco = (titulo, linhas) => ({ titulo, linhas: linhas.filter(Boolean) })

      const id = this.details.identification || {}
      const resp = this.details.responsible || {}
      const act = this.details.activity || {}
      const end = this.details.address || {}
      const org = this.details.origin || {}

      const atendimento = [
        act.serves_in_person ? 'presencial' : null,
        act.serves_at_home ? 'a domicílio' : null,
        act.serves_online ? 'online' : null,
      ].filter(Boolean).join(', ')

      const enderecoLinha = end.street_name
        ? `${end.street_name}, ${end.street_number} — ${end.district_name}`
        : null
      const cidadeLinha = end.city ? `${end.city}/${end.federation_unit || ''}` : null

      return [
        bloco('Identificação', [
          linha('Tipo', id.person_type_label),
          linha(id.document_label || 'Documento', id.document),
          linha(this.personType === 'LE' ? 'Razão social' : 'Nome', id.company_name),
          linha(this.personType === 'LE' ? 'Nome fantasia' : 'Nome público', id.trading_name),
          linha('Descrição', id.description),
        ]),
        bloco('Responsável', [
          linha('Nome', resp.name),
          linha('E-mail', resp.email),
          linha('Telefone', resp.phone),
          linha('CPF', resp.document),
          linha('Nascimento', resp.birthdate),
        ]),
        bloco('Atuação', [
          linha('Modalidades', (act.modalities || []).join(', ')),
          linha('Atendimento', atendimento),
          linha('Raio de atendimento', act.service_radius_km ? `${act.service_radius_km} km` : null),
        ]),
        bloco('Endereço', [
          linha('CEP', end.postal_code),
          linha('Logradouro', enderecoLinha),
          linha('Cidade', cidadeLinha),
        ]),
        bloco('Origem', [
          linha('Veio de', this.originLabel(org.source)),
          linha('Enviado em', this.formatDate(org.submitted_at || id.submitted_at)),
        ]),
      ]
    },
  },
  methods: {
    switchType(type) {
      if (this.personType !== type) this.personType = type
    },
    /** Mesmas colunas nas duas abas; muda o rótulo conforme a natureza. */
    columnLabel(originalColumnName) {
      const overrides = this.personType === 'NE'
        ? { cnpj: 'CPF', company_name: 'Nome', trading_name: 'Nome público' }
        : { cnpj: 'CNPJ', company_name: 'Razão social', trading_name: 'Nome fantasia' }

      return overrides[originalColumnName] || this.$t(`establishment.${originalColumnName}`)
    },
    kycLabel(status) {
      return this.kycLabels[status] || 'Pendente'
    },
    kycVariant(status) {
      if (status === 'AP') return 'light-success'
      if (status === 'RJ') return 'light-danger'
      return 'light-warning'
    },
    /**
     * Rótulo da procedência. Sem lead vinculado o cadastro não veio do wizard
     * público — é o profissional criado pela equipe, pelo backoffice.
     */
    originLabel(source) {
      if (source === 'MP') return 'Convite no marketplace (auto-cadastro)'
      if (source === 'DI') return 'Cadastro público, acesso direto'
      return 'Cadastro feito pela equipe Hubee'
    },
    formatDate(iso) {
      if (!iso) return null
      const data = new Date(iso)
      return isNaN(data.getTime()) ? null : data.toLocaleString('pt-BR')
    },
    toggleDetails() {
      if (this.showDetails) {
        this.showDetails = false
        return
      }

      this.showDetails = true

      // Busca uma vez por cadastro aberto: cada leitura gera uma linha no log
      // de acesso a dado pessoal, e abrir/fechar o painel não é um acesso novo.
      if (this.details || this.loadingDetails) return

      this.loadingDetails = true
      this.legalEntityService.kycDetails(this.selected.id).then(
        data => { this.details = data },
        error => {
          this.details = null
          this.showErrors(null, error)
        }
      ).finally(() => { this.loadingDetails = false })
    },
    openReview(row) {
      this.selected = row
      this.notes = ''
      this.checklist = []
      this.details = null
      this.showDetails = false
      this.showReview = true

      this.legalEntityService.kycChecklist(row.id).then(
        data => { this.checklist = data },
        error => { this.showErrors(null, error) }
      )
    },
    approve() {
      this.$vs.loading()
      this.legalEntityService.kycApprove(this.selected.id, this.notes || null).then(
        () => {
          this.$vs.loading.close()
          this.notifySuccess(this.$vs, 'Cadastro aprovado. O responsável foi avisado por e-mail.')
          this.afterDecision()
        },
        error => {
          this.$vs.loading.close()
          this.showErrors(null, error)
        }
      )
    },
    reject() {
      if (this.isEmpty(this.notes)) {
        this.notifyWarning(this.$vs, 'Informe o motivo da recusa.')
        return
      }

      this.$vs.loading()
      this.legalEntityService.kycReject(this.selected.id, this.notes).then(
        () => {
          this.$vs.loading.close()
          this.notifySuccess(this.$vs, 'Cadastro recusado. O motivo foi enviado por e-mail ao responsável.')
          this.afterDecision()
        },
        error => {
          this.$vs.loading.close()
          this.showErrors(null, error)
        }
      )
    },
    afterDecision() {
      this.showReview = false
      this.selected = null
      this.details = null
      this.showDetails = false
      this.$refs.grid.fetchGridData(true)
    },
  },
  beforeMount() {
    this.professionalService = ProfessionalService.build(this.$vs)
    this.legalEntityService = LegalEntityService.build(this.$vs)
  },
}
</script>

<style lang="scss" scoped>
.kyc-details {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 0.5rem;
  padding: 0.9rem 1rem;
  background: rgba(115, 103, 240, 0.04);
}

.kyc-details-table {
  width: 100%;

  th {
    /* Rótulo estreito e alinhado ao topo: descrição de estabelecimento é texto
       longo e não pode empurrar a coluna da esquerda. */
    width: 11rem;
    vertical-align: top;
    font-weight: 600;
    padding: 0.15rem 0.75rem 0.15rem 0;
    white-space: nowrap;
  }

  td {
    vertical-align: top;
    padding: 0.15rem 0;
    word-break: break-word;
  }
}

@media (max-width: 575px) {
  .kyc-details-table th { width: 7.5rem; white-space: normal; }
}
</style>
